import {FC, useEffect, useRef, useState} from 'react'
import {useAuth} from '../../auth'
import {AuthModel} from '../../auth/core/_models'
import {CategoryAccess, getCategoryAccess} from './stuffidAccessApi'

/** Who is signed in, as the bar names them: their name, else their mobile. */
export const stuffIdAccountName = (user?: Pick<AuthModel, 'fn' | 'ln' | 'mbl'> | null): string => {
  const name = `${user?.fn ?? ''} ${user?.ln ?? ''}`.trim()
  return name || user?.mbl?.trim() || 'حساب شما'
}

/** What opens the categories for the signed-in person, in a few words; undefined when nothing worth saying. */
export const stuffIdAccessLabel = (access?: CategoryAccess | null): string | undefined => {
  if (!access?.open) return undefined
  switch (access.openBy) {
    case 'subscription':
      return access.subscription?.endsAtLabel
        ? `اشتراک دسته‌بندی‌ها تا ${access.subscription.endsAtLabel}`
        : 'اشتراک دسته‌بندی‌ها فعال است'
    case 'business':
      return 'دسته‌بندی‌ها از طریق کسب‌وکار شما'
    default:
      return undefined
  }
}

type Props = {
  panelUrl: string
  registrationUrl: string
  /** ASA's own links for a visitor who is not signed in («ورود به پنل»، «ثبت‌نام رایگان»). */
  showGuestLinks: boolean
  /**
   * What the signed-in person's subscription opens, in a few words; the goods/services ID categories' by default, the
   * tax-file inquiry's on the inquiry page.
   */
  readPlan?: () => Promise<string | undefined>
}

const readCategoryPlan = async () => stuffIdAccessLabel(await getCategoryAccess())

/**
 * The top of the goods/services ID page (user request 1405/07/30): a visitor who signed in with a mobile, and bought a
 * subscription, sees who they are and until when, and can sign out; anyone else sees ASA's sign-in links as before.
 * The public inquiry page uses it too.
 */
export const StuffIdAccountBar: FC<Props> = ({
  panelUrl,
  registrationUrl,
  showGuestLinks,
  readPlan = readCategoryPlan,
}) => {
  const {currentUser, logout} = useAuth()
  const [plan, setPlan] = useState<string>()
  const [leaving, setLeaving] = useState(false)
  const userId = currentUser?.id
  // The page may pass a fresh function on every render; the read uses the latest one without running again.
  const readPlanRef = useRef(readPlan)
  readPlanRef.current = readPlan

  useEffect(() => {
    setPlan(undefined)
    if (!userId) return
    let alive = true
    readPlanRef
      .current()
      .then((label) => alive && setPlan(label))
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [userId])

  if (!currentUser) {
    return showGuestLinks ? (
      <nav className='asa-stuffid__bar-links' aria-label='آسا'>
        <a href={panelUrl} className='asa-stuffid__bar-link'>
          ورود به پنل
        </a>
        <a href={registrationUrl} className='btn btn-primary asa-stuffid__bar-cta'>
          ثبت‌نام رایگان
        </a>
      </nav>
    ) : null
  }

  return (
    <nav className='asa-stuffid__account' aria-label='حساب کاربری'>
      <span className='asa-stuffid__account-who'>
        <i className='las la-user-circle' aria-hidden='true' />
        <span className='asa-stuffid__account-text'>
          <span className='asa-stuffid__account-name'>{stuffIdAccountName(currentUser)}</span>
          {plan && <span className='asa-stuffid__account-plan'>{plan}</span>}
        </span>
      </span>
      {(currentUser.cpi ?? 0) > 0 && (
        <a href={panelUrl} className='asa-stuffid__bar-link'>
          پنل
        </a>
      )}
      <button
        type='button'
        className='btn btn-light btn-sm asa-stuffid__account-logout'
        disabled={leaving}
        onClick={async () => {
          setLeaving(true)
          // The server drops the session cookie first: a reload before that signed the person straight back in.
          await logout()
          // The categories' lock and the search follow the session: start again signed out.
          window.location.reload()
        }}
      >
        {leaving ? (
          <span className='spinner-border spinner-border-sm' aria-hidden='true' />
        ) : (
          <i className='las la-sign-out-alt' aria-hidden='true' />
        )}
        خروج
      </button>
    </nav>
  )
}
